import {Alert, List, ListItem, ListItemText} from "@mui/material";
import React from "react";

// 'flat_door_building' reads as noise in an alert; 'Flat door building' does not.
function humanizeField(field) {
    const words = field.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function isFieldErrors(data) {
    return Boolean(data) && !Array.isArray(data) && typeof data === 'object' && Object.keys(data).length > 0;
}

// A nested serializer does not answer with a list of strings. A list serializer
// answers with one entry per row - {"invoices": [{}, {"exchange_rate": ["..."]}]} -
// and a failure on the list itself answers with a dict -
// {"invoices": {"non_field_errors": ["This list may not be empty."]}}. Both used to
// be flattened with join(' '), which stringifies an object as "[object Object]", so
// the CA was told "Invoices: [object Object]" and never which invoice or what was
// wrong with it. Walk the whole structure instead and keep the row number.
function collectErrors(value, path, out) {
    if (value === null || value === undefined) {
        return out;
    }
    if (typeof value === 'string') {
        out.push({path, message: value});
        return out;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return out;
        }
        if (value.every(entry => typeof entry === 'string')) {
            out.push({path, message: value.join(' ')});
            return out;
        }
        // One entry per row, and the rows that passed come back as {}.
        value.forEach((entry, index) => collectErrors(entry, path.concat({row: index + 1}), out));
        return out;
    }
    if (typeof value === 'object') {
        Object.keys(value).forEach(key => collectErrors(
            // 'non_field_errors' names no field the CA can see, so it stays out of the label.
            value[key], key === 'non_field_errors' ? path : path.concat({field: key}), out
        ));
        return out;
    }
    out.push({path, message: String(value)});
    return out;
}

function formatLabel(path) {
    return path
        .map(part => (part.row === undefined ? humanizeField(part.field) : `#${part.row}`))
        .join(' ');
}

function fieldErrorLines(fieldErrors) {
    const out = [];
    Object.keys(fieldErrors).forEach(field => collectErrors(fieldErrors[field], [{field}], out));
    return out.map(({path, message}) => `${formatLabel(path)}: ${message}`);
}

export default function APIError({error}) {
    let message;
    let severity = "error";

    if (error.code === "ERR_NETWORK") {
        message = "Oops! Looks like our server took a coffee break ☕️. We're on it!"
        severity = "warning"
    } else if (error.code === "ERR_BAD_RESPONSE" && error.response.status === 500) {
        message = "Server's on vacation, but we're summoning the IT wizards!"
        severity = "warning"
    } else if (error.response.status === 400 && error.response.data.detail) {
        let detailErrors = error.response.data.detail;
        message = (
            <List disablePadding>
                {
                    detailErrors && detailErrors.map((detailError, index) => (
                        <ListItem key={index} sx={{p: 0}} disablePadding>
                            <ListItemText
                                primary={detailError}
                            />
                        </ListItem>
                    ))
                }
            </List>
        )
    } else if (error.response.status === 400 && isFieldErrors(error.response.data)) {
        // A field-level 400 - {"pincode": ["This field is required."]} - has no
        // 'detail' key, so nothing was rendered here at all and the save looked
        // like it simply did nothing. Array-shaped 400s belong to
        // AdditionalAPIError and are left to it.
        const lines = fieldErrorLines(error.response.data);
        // A row that passed comes back as {}, so a payload can carry keys and still
        // hold no message. Rendering that put an empty red box on the screen.
        if (lines.length > 0) {
            message = (
                <List disablePadding>
                    {lines.map((line, index) => (
                        <ListItem key={index} sx={{p: 0}} disablePadding>
                            <ListItemText primary={line}/>
                        </ListItem>
                    ))}
                </List>
            )
        }
    } else if (error.response.status > 400) {
        message = error.response.data.detail
    }

    if (message) {
        return <Alert severity={severity} sx={{alignItems: 'center'}}>{message}</Alert>
    }

    return <></>;
}


export function AdditionalAPIError({apiError, field='Invoice'}) {

    if (apiError.response.status !== 400 || !Array.isArray(apiError.response.data)){
        return <></>
    }

    return (
        <>
            <Alert icon={false} severity={'error'}>
                <List disablePadding>
                    {apiError.response.data.map((errors, rowIndex) => (
                        <ListItem disablePadding key={rowIndex} sx={{flexDirection: 'column', alignItems: 'start'}}>
                            {
                                Object.keys(errors).length !== 0 &&
                                <>
                                    <ListItemText>{field} - {rowIndex + 1}</ListItemText>
                                    <List disablePadding>
                                        {Object.keys(errors).map((key, errIndex) => (
                                            <React.Fragment key={`${rowIndex}-${errIndex}`}>
                                                <ListItem sx={{py: 0}}>
                                                    <ListItemText>{errors[key]}</ListItemText>
                                                </ListItem>
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </>
                            }
                        </ListItem>
                    ))}
                </List>
            </Alert>
        </>
    )
}
